/**
 * SecurityPage — public "How we protect your data" page (s92). Truthful, user-facing
 * claims only (no key-derivation params / internal architecture). Shared PublicLayout.
 */
import { PublicLayout, CONTAINER } from "@/components/public/PublicLayout";
import { Shield, Lock, Mail, Smartphone, Clock } from "lucide-react";
import { FONT, SAGE, WARM } from "@shared/brand";
import { Seo } from "@/components/public/Seo";

const CLAIMS: { icon: typeof Shield; title: string; body: string }[] = [
  {
    icon: Shield,
    title: "Sensitive data stays on your device",
    body: "Your most sensitive information — passwords, PINs, government IDs — is kept in a file on your own computer and never reaches our servers. Set a passphrase and the file is encrypted so strongly that no one, including us, can read it. Without one, the file opens directly, so your executor needs nothing extra.",
  },
  {
    icon: Lock,
    title: "Server-side storage",
    body: "Data stored on our servers (account numbers, policy numbers) is held in our database with encryption at rest provided by our infrastructure providers. Field-level encryption of these values is in development and is not yet in place.",
  },
  {
    icon: Mail,
    title: "Email verification",
    body: "Every account requires email verification before access is granted. Unverified accounts cannot log in.",
  },
  {
    icon: Smartphone,
    title: "Two-factor authentication",
    body: "Optional SMS-based two-factor authentication adds an extra layer of protection to your account.",
  },
  {
    icon: Clock,
    title: "Session security",
    body: "Secure, HTTP-only cookies with automatic session expiry after 30 minutes of inactivity. All connections are encrypted via TLS.",
  },
];

export default function SecurityPage() {
  return (
    <PublicLayout>
      <Seo path="/security" />
      <section style={{ background: `radial-gradient(110% 80% at 50% 0%, ${SAGE[50]} 0%, ${WARM.cream} 60%)`, padding: "72px 32px 88px" }}>
        <div className={CONTAINER} style={{ maxWidth: 900 }}>
          <div style={{ textAlign: "center", maxWidth: 660, margin: "0 auto 48px" }}>
            <h1 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 44, lineHeight: 1.1, letterSpacing: "-0.015em", color: WARM.ink, margin: "0 0 16px", textWrap: "balance" }}>
              How we protect your data
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.62, color: "#514D44", margin: 0 }}>
              An estate plan holds some of the most sensitive information about your life. Protecting it isn't an afterthought — it shapes how the whole product is built. Here's how we keep it safe.
            </p>
          </div>

          <div className="grid gap-5 grid-cols-1 min-[680px]:grid-cols-2">
            {CLAIMS.map(({ icon: Icon, title, body }) => (
              <div key={title} style={{ background: WARM.white, border: "1px solid #E7E1D5", borderRadius: 14, padding: "28px 26px" }}>
                <div style={{ width: 46, height: 46, borderRadius: 11, background: SAGE[50], display: "flex", alignItems: "center", justifyContent: "center", color: SAGE[600], marginBottom: 16 }}>
                  <Icon size={22} strokeWidth={1.7} />
                </div>
                <h2 style={{ fontFamily: FONT.serif, fontWeight: 500, fontSize: 20, lineHeight: 1.25, color: WARM.ink, margin: "0 0 9px" }}>{title}</h2>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: "#5A554A" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
